import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Connector } from '../Connector'
import { Match } from '../Match'
import { computeFullLayout } from '../utils'
import styles from './brackets.module.scss'
import { Bracket, Seed } from 'core/models/Bracket'
import { getFormatName } from 'core/utils/StringHelper'
import { buildDependencyTextMap } from 'core/utils/Brackets'

const HEADER_HEIGHT = 40
const UPPER_TITLE_GAP = 30
const LOWER_TITLE_GAP = 30
const CONTENT_TOP = HEADER_HEIGHT + UPPER_TITLE_GAP
const PAN_PADDING = 20

interface BracketsProps {
  brackets: Bracket
  modeOverride?: 'single' | 'double'
  zoomMin?: number
  zoomMax?: number
  background?: string
  connectorColor?: string
  connectorHighlightColor?: string
  showRoundTitles?: boolean
  fullHeight?: boolean
  alwaysRefitOnResize?: boolean
  onMatchClick?: (seed: Seed) => void
}

const Brackets: React.FC<BracketsProps> = ({
  brackets,
  modeOverride,
  zoomMin = 0.2,
  zoomMax = 2.5,
  background = '#131925',
  connectorColor = 'rgb(56, 69, 87)',
  showRoundTitles = true,
  fullHeight = false,
  alwaysRefitOnResize = false,
  connectorHighlightColor = 'rgb(126, 139, 157)',
  onMatchClick,
}) => {
  const layout = useMemo(() => {
    if (!brackets?.upper)
      return { all: [], bracketWidth: 0, bracketHeight: 0, width: 0, height: 0 }

    return computeFullLayout(
      { ...brackets, mode: brackets.mode || modeOverride },
      { bracketWidth: 340, bracketHeight: 125 }
    )
  }, [brackets, modeOverride])

  const containerRef = useRef<HTMLDivElement>(null)
  const worldRef = useRef<HTMLDivElement>(null)
  const [translate, setTranslate] = useState({ x: 0, y: 0 })
  const [scale, setScale] = useState(1)
  const [dragging, setDragging] = useState(false)
  const [autoFit, setAutoFit] = useState(true)
  const [fittedScale, setFittedScale] = useState(1)
  const translateRef = useRef(translate)
  const scaleRef = useRef(1)
  const teamMap = useMemo(() => {
    return buildDependencyTextMap(brackets)
  }, [brackets.mode])

  const [hoveredFrom, setHoveredFrom] = useState<{
    id: string
    index: 0 | 1
  } | null>(null)

  useEffect(() => setAutoFit(true), [brackets])
  useEffect(() => {
    translateRef.current = translate
  }, [translate])

  const { all, bracketWidth, bracketHeight, width, height } = layout

  const teamKeyOf = (t: any): string | null =>
    t ? (t.id ?? t._id ?? t.uuid ?? t.teamId ?? t.slug ?? t.name ?? null) : null

  const hoveredTeamKey = useMemo(() => {
    if (!hoveredFrom) return null
    const src = all.find((b) => b.id === hoveredFrom.id)
    const team = src?.seed?.teams?.[hoveredFrom.index]
    return teamKeyOf(team)
  }, [all, hoveredFrom])

  const roundColumns = useMemo(() => {
    if (!showRoundTitles || !brackets?.upper?.length) return []
    interface Col {
      key: string
      x: number
      y: number
      title: string
      format?: string
      region: 'upper' | 'lower' | 'final' | 'third'
    }
    const cols: Col[] = []

    const collectRegion = (
      region: Col['region'],
      bandY: number,
      withFormat = false
    ) => {
      const regionBrs = all.filter((b) => b.region === region)
      if (!regionBrs.length) return
      const byRound = new Map<number, number>()
      for (const b of regionBrs) {
        byRound.set(
          b.roundIndex,
          Math.min(byRound.get(b.roundIndex) ?? Infinity, b.x)
        )
      }
      const ordered = Array.from(byRound.keys()).sort((a, b) => a - b)
      for (let idx = 0; idx < ordered.length; idx++) {
        const ri = ordered[idx]
        const srcRound =
          region === 'upper'
            ? brackets.upper[idx]
            : region === 'lower'
              ? brackets.lower?.[idx]
              : region === 'final'
                ? brackets.final?.[idx]
                : brackets.thirdPlace?.[idx]
        if (!srcRound) continue
        cols.push({
          key: `${region}-${ri}`,
          x: byRound.get(ri)!,
          y: bandY,
          title: srcRound.title,
          format: withFormat ? getFormatName(srcRound.format) : undefined,
          region,
        })
      }
    }

    collectRegion('upper', 0, true)
    const lowerBrs = all.filter((b) => b.region === 'lower')
    if (lowerBrs.length) {
      const lowerTop = Math.min(...lowerBrs.map((b) => b.y))
      const lowerBandY =
        CONTENT_TOP + lowerTop - HEADER_HEIGHT - LOWER_TITLE_GAP
      collectRegion('lower', lowerBandY, true)
    }
    collectRegion('final', 0, true)

    const thirdBrs = all.filter((b) => b.region === 'third')
    if (thirdBrs.length) {
      const thirdTop = Math.min(...thirdBrs.map((b) => b.y))
      const thirdBandY = CONTENT_TOP + thirdTop - HEADER_HEIGHT
      collectRegion('third', thirdBandY, false)
    }

    return cols
  }, [all, brackets, showRoundTitles])

  const applyTransform = () => {
    const el = worldRef.current
    if (!el) return
    const { x, y } = translateRef.current
    el.style.transform = `translate(${x}px, ${y}px) scale(${scaleRef.current})`
  }

  const clampTranslate = (tx: number, ty: number, s: number) => {
    const container = containerRef.current
    if (!container) return { x: tx, y: ty }
    const viewW = container.clientWidth
    const viewH = container.clientHeight
    const contentW = width * s
    const contentH = (height + CONTENT_TOP) * s
    const PAD = PAN_PADDING

    if (contentW + PAD * 2 <= viewW) {
      tx = (viewW - contentW) / 2
    } else {
      const maxTx = PAD
      const minTx = viewW - contentW - PAD
      tx = Math.min(maxTx, Math.max(minTx, tx))
    }

    if (contentH + PAD * 2 <= viewH) {
      ty = (viewH - contentH) / 2
    } else {
      const maxTy = PAD
      const minTy = viewH - contentH - PAD
      ty = Math.min(maxTy, Math.max(minTy, ty))
    }

    return { x: tx, y: ty }
  }

  const dragState = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
    origX: 0,
    origY: 0,
  })

  const touchState = useRef({
    isPinching: false,
    initialDistance: 0,
    initialScale: 1,
    centerX: 0,
    centerY: 0,
  })

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button !== 0) return
    const target = e.target as HTMLElement
    if (target.closest('.' + styles.controls)) return

    if (e.pointerType === 'touch' && touchState.current.isPinching) return
    dragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: translateRef.current.x,
      origY: translateRef.current.y,
    }
    setDragging(true)
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragState.current.dragging) return
    const dx = e.clientX - dragState.current.startX
    const dy = e.clientY - dragState.current.startY
    const next = clampTranslate(
      dragState.current.origX + dx,
      dragState.current.origY + dy,
      scaleRef.current
    )
    translateRef.current = next
    applyTransform()
  }

  const onPointerUp = () => {
    if (!dragState.current.dragging) return
    dragState.current.dragging = false
    setDragging(false)
    setTranslate({ ...translateRef.current })
  }

  const zoomAroundCenter = (multiplier: number) => {
    const container = containerRef.current
    if (!container) return
    const anchorScreenX = container.clientWidth / 2
    const anchorScreenY = container.clientHeight / 2
    const prevScale = scaleRef.current
    const dynamicZoomMin = Math.min(zoomMin, fittedScale * 0.8)
    const nextScale = Math.min(
      zoomMax,
      Math.max(dynamicZoomMin, prevScale * multiplier)
    )
    const prevTx = translateRef.current.x
    const prevTy = translateRef.current.y
    const contentX = (anchorScreenX - prevTx) / prevScale
    const contentY = (anchorScreenY - prevTy) / prevScale
    const nextTx = anchorScreenX - contentX * nextScale
    const nextTy = anchorScreenY - contentY * nextScale
    const clamped = clampTranslate(nextTx, nextTy, nextScale)
    scaleRef.current = nextScale
    translateRef.current = clamped
    setScale(nextScale)
    setTranslate(clamped)
    applyTransform()
  }

  const handleZoomIn = () => zoomAroundCenter(1.15)
  const handleZoomOut = () => zoomAroundCenter(1 / 1.15)

  const handleReset = () => {
    setAutoFit(false)
    const resetScale = fittedScale || 1
    scaleRef.current = resetScale
    translateRef.current = { x: 0, y: 0 }
    setScale(resetScale)
    setTranslate({ x: 0, y: 0 })
    applyTransform()
  }

  const fitAll = useCallback(() => {
    const container = containerRef.current
    if (!container) return
    const margin = 40
    const contentHeight = height + CONTENT_TOP
    const sx = (container.clientWidth - margin) / width
    const sy = (container.clientHeight - margin) / contentHeight
    const s = Math.min(1, Math.max(0.2, Math.min(sx, sy)))
    const initialTx = margin / 2
    const initialTy = margin / 2
    const clamped = clampTranslate(initialTx, initialTy, s)
    scaleRef.current = s
    translateRef.current = clamped
    setScale(s)
    setFittedScale(s)
    setTranslate(clamped)
    requestAnimationFrame(applyTransform)
  }, [width, height])

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      if (alwaysRefitOnResize || autoFit) fitAll()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [fitAll, autoFit, alwaysRefitOnResize])

  useLayoutEffect(() => {
    if (autoFit) fitAll()
  }, [width, height, autoFit, fitAll])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        zoomAroundCenter(1 + -e.deltaY * 0.01)
      }
    }
    container.addEventListener('wheel', onWheel, { passive: false })
    return () => container.removeEventListener('wheel', onWheel)
  }, [])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const getDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.hypot(dx, dy)
    }

    const getCenter = (touches: TouchList) => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
      }
    }

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        const distance = getDistance(e.touches)
        const center = getCenter(e.touches)
        touchState.current = {
          isPinching: true,
          initialDistance: distance,
          initialScale: scaleRef.current,
          centerX: center.x,
          centerY: center.y,
        }
        dragState.current.dragging = false
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && touchState.current.isPinching) {
        e.preventDefault()
        const distance = getDistance(e.touches)
        const scale =
          touchState.current.initialScale *
          (distance / touchState.current.initialDistance)
        const dynamicZoomMin = Math.min(zoomMin, fittedScale * 0.8)
        const nextScale = Math.min(zoomMax, Math.max(dynamicZoomMin, scale))

        const { centerX, centerY } = touchState.current
        const prevScale = scaleRef.current
        const prevTx = translateRef.current.x
        const prevTy = translateRef.current.y

        const contentX = (centerX - prevTx) / prevScale
        const contentY = (centerY - prevTy) / prevScale
        const nextTx = centerX - contentX * nextScale
        const nextTy = centerY - contentY * nextScale

        const clamped = clampTranslate(nextTx, nextTy, nextScale)
        scaleRef.current = nextScale
        translateRef.current = clamped
        setScale(nextScale)
        setTranslate(clamped)
        applyTransform()
      }
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (touchState.current.isPinching) {
        touchState.current.isPinching = false
        setAutoFit(false)
      }
    }

    container.addEventListener('touchstart', onTouchStart, { passive: false })
    container.addEventListener('touchmove', onTouchMove, { passive: false })
    container.addEventListener('touchend', onTouchEnd)
    container.addEventListener('touchcancel', onTouchEnd)

    return () => {
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', onTouchEnd)
      container.removeEventListener('touchcancel', onTouchEnd)
    }
  }, [zoomMin, zoomMax, fittedScale])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const onHover = (e: Event) => {
      const ce = e as CustomEvent<{ fromId?: string; index?: 0 | 1 | null }>
      const fromId = ce.detail?.fromId
      const index = ce.detail?.index
      if (fromId && (index === 0 || index === 1))
        setHoveredFrom({ id: fromId, index })
      else setHoveredFrom(null)
    }
    el.addEventListener('brackets:hover-team', onHover as EventListener)
    return () =>
      el.removeEventListener('brackets:hover-team', onHover as EventListener)
  }, [])

  if (!layout.all.length) {
    return (
      <div style={{ color: 'white', padding: 20 }}>No bracket data found.</div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`${styles.root} ${dragging ? styles.dragging : ''}`}
      style={{
        ['--brackets-bg' as any]: background,
        ...(fullHeight && { height: '100vh', maxHeight: '100vh' }),
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerUp}
    >
      <div className={styles.controls}>
        <button className={styles.btn} onClick={handleZoomIn}>
          +
        </button>
        <button className={styles.btn} onClick={handleZoomOut}>
          -
        </button>
        <button
          className={styles.btn}
          onClick={() => {
            setAutoFit(true)
            fitAll()
          }}
        >
          Fit
        </button>
        <button className={styles.btn} onClick={handleReset}>
          Reset
        </button>
        <div className={styles.zoomHint}>
          <span>Ctrl + Scroll to zoom</span>
        </div>
      </div>

      <div
        ref={worldRef}
        className={styles.world}
        style={{
          width,
          height: height + CONTENT_TOP,
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
        }}
        data-hovered-from={hoveredFrom?.id || ''}
      >
        {showRoundTitles && (
          <div className={styles.titlesLayer}>
            {roundColumns.map((rc) => (
              <div
                key={rc.key}
                className={styles.roundTitle}
                style={{
                  left: rc.x,
                  top: rc.y,
                  width: bracketWidth,
                  height: HEADER_HEIGHT,
                }}
              >
                <div className={styles.roundTitleText}>{rc.title}</div>
                {rc.region !== 'third' && rc.format && (
                  <div className={styles.roundFormat}>{rc.format}</div>
                )}
              </div>
            ))}
          </div>
        )}

        <svg
          width={width}
          height={height}
          className={styles.svgLayer}
          style={{ top: CONTENT_TOP }}
        >
          <Connector
            brackets={all}
            bracketWidth={bracketWidth}
            bracketHeight={bracketHeight}
            color={connectorColor}
            hoveredFromId={hoveredFrom?.id}
            hoveredFromIndex={hoveredFrom?.index}
            connectorHighlightColor={connectorHighlightColor}
          />
          {(() => {
            const finalMatches = all.filter(
              (b) => b.region === 'final' && b.roundIndex === 0
            )
            if (finalMatches.length === 2) {
              const [first, second] = finalMatches
              return (
                <line
                  x1={first.x + bracketWidth}
                  y1={first.y + bracketHeight / 2}
                  x2={second.x}
                  y2={second.y + bracketHeight / 2}
                  stroke={connectorColor}
                  strokeWidth={3}
                />
              )
            }
            return null
          })()}
        </svg>

        <div className={styles.matches} style={{ top: CONTENT_TOP }}>
          {all.map((positionedBracket) => (
            <Match
              key={positionedBracket.id}
              futureTeamMap={teamMap}
              positionedBracket={positionedBracket}
              width={bracketWidth}
              height={bracketHeight}
              onFocus={() => {}}
              onClick={(seed) => onMatchClick?.(seed)}
              hoveredTeamKey={hoveredTeamKey || undefined}
            />
          ))}
          {/* Label for reset match */}
          {(() => {
            const finalMatches = all.filter(
              (b) => b.region === 'final' && b.roundIndex === 0
            )
            if (finalMatches.length === 2) {
              const second = finalMatches[1]
              return (
                <div
                  className={styles.resetLabel}
                  style={{
                    position: 'absolute',
                    left: second.x,
                    top: second.y - 32,
                    width: bracketWidth,
                    textAlign: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  Reset Final
                </div>
              )
            }
            return null
          })()}
        </div>
      </div>
    </div>
  )
}

export default Brackets
